import './dashboard-bronze.scss';
import React, {useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {useStores} from '../store';
import usePromiseFeedback from '../shared/promise-feedback/usePromiseFeedback';
import {Navigate, useParams} from 'react-router-dom';
import {RightPanelBronze} from './right-panel-bronze';
import Loader from '../shared/loader/loader';
import {CircuitType, LevelName, MainPages} from 'src/utils/enums';
import {MicroBar} from '../shared/microbar/microBar';
import {SERVICE_STATUS_POLLING} from 'src/utils/constants';
import AppHeader from '../features/app-header/app-header';
import ShieldLogo from '@svg/shield-logo.svg?react';
import {DashboardBronzeContent} from '@src/components/bronze/dashboard-bronze-content';
import {usePageViewEvent} from '@src/components/shared/hooks/use-page-view-event';

export const DashboardBronze: React.FC = observer(() => {
    const { customerStore, fwVersionStore, fwVersionBronzeStore, uiStore, userPreferencesStore } = useStores();
    const [isReady, setIsReady] = useState(false);
    const [activeBronzeTab, setActiveBronzeTab] = useState('devices');
    const [promiseFeedbackFactory, promiseContextHolder] = usePromiseFeedback();
    const params = useParams();

    usePageViewEvent(MainPages.BRONZE);

    useEffect(() => {
        uiStore.setMainPage(MainPages.BRONZE);
        return () => {
            fwVersionStore.reset();
            fwVersionBronzeStore.reset();
            customerStore.reset();
        };
    }, []);

    useEffect(() => {
        if (!customerStore.isLoading && !customerStore.data) {
            promiseFeedbackFactory({
                promise: customerStore.load(),
                notifObj: {show: false}
            });
        }
    }, []);

    useEffect(() => {
        if (customerStore.customerId && customerStore.serviceId
            && customerStore.selectedLocation?.serviceId === customerStore.serviceId
            && customerStore.selectedLocation?.serviceLevel === LevelName.BRONZE) {
            void Promise.all([fwVersionStore.loadVersionsList(), userPreferencesStore.load(), userPreferencesStore.loadReportsList()]);
        }
    }, [customerStore.customerId, customerStore.serviceId, customerStore.selectedLocation]);

    useEffect(() => {
        setIsReady(false);
        if (customerStore.data) {
            void customerStore.setCustomerAndLocation(Number(params.customerId), Number(params.serviceId))
                .then(() => setIsReady(true))
            ;
        }

    }, [customerStore.data, params.customerId, params.serviceId]);

    useEffect(() => {
        if (isReady && customerStore.selectedLocation?.serviceLevel === LevelName.BRONZE) {
            promiseFeedbackFactory({
                promise: fwVersionBronzeStore.loadBronzeVersionData(),
                notifObj: {show: false},
            });

            void customerStore.getServiceStatus();
            const statusTimer: any = setInterval(() =>
                customerStore.getServiceStatus(), SERVICE_STATUS_POLLING);

            return () => {
                clearInterval(statusTimer);
            };
        }

    }, [isReady, customerStore.selectedCustomer]);

    const bronzeContent = () => {
        const isMobileCircuit = customerStore.selectedLocation?.circuitType === CircuitType.MOBILE_CRU;
        const showRightPanel = !isMobileCircuit || activeBronzeTab !== 'devices';
        return <>
            <MicroBar/>
            <div className="dashboard-bronze">
                <AppHeader isBronze={true} />
                <div className="dashboard-bronze-wrapper">
                    <div className="dashboard-bronze-content">
                        <div className="dashboard-bronze-header">
                            <ShieldLogo />
                            <span className="title">Dynamic Defense is Active</span>
                            <span className="desc">{isMobileCircuit
                                ? 'Proactive domains block list powered by AT&T'
                                : 'Proactive global IP and domains block list powered by AT&T'}</span>
                        </div>
                        {isReady && fwVersionBronzeStore.selectedFwVersion &&
                            <DashboardBronzeContent onTabChange={setActiveBronzeTab}/>}
                    </div>
                    {showRightPanel && <RightPanelBronze/>}
                </div>
            </div>
            {promiseContextHolder}
        </>;
    };

    const getDisplayContent = () => {
        let result;
        if (isReady && customerStore.selectedLocation) {
            if (customerStore.selectedLocation?.serviceLevel === LevelName.BRONZE) {
                result = bronzeContent();
            } else {
                result = <Navigate to={`/${params.customerId}/${params.serviceId}/dashboard`}/>;
            }
        } else {
            result = <Loader type={'full-screen'}/>;
        }
        return result;
    };

    return getDisplayContent();
});